import { FC, Fragment, useRef } from "react";
import { Modal as UIModal } from "src/components/ui";

interface InvestModalProps {
  setModalVisible: any;
  isModalVisible: boolean;
  children: any;
  /** "buy" or "sell" — what the primary action does. */
  side?: string;
  symbol?: string;
}

/**
 * The order dialog.
 *
 * It was a pass-through to the generic invest shell with `title={null}`,
 * `closable={false}` and `footer={null}`: no heading, no close, and no
 * footer, opened at 60% of the viewport whatever that came to. So the dialog
 * that places a real trade was the one dialog in the product that never said
 * what it was — a reader arrived at a form with a price widget at the top
 * and had to read the button at the bottom to learn whether they were buying
 * or selling.
 *
 * Now it is the product's dialog: named after the action and the symbol, with
 * a close, and its actions in the footer band where every other dialog keeps
 * them.
 *
 * The actions belong to the STEP rather than to the shell, and are rendered
 * into the band through the shell's slot — the form puts Cancel and Review
 * there, the summary replaces them with Back and Confirm. A footer declared
 * here could only ever describe one of the two.
 */
export const InvestModal: FC<InvestModalProps> = ({
  setModalVisible,
  isModalVisible,
  children,
  side,
  symbol,
}) => {
  const action = side === "sell" ? "Sell" : "Buy";

  /**
   * Every press of Buy is a NEW form.
   *
   * A count of visits, bumped in the render that opens the dialog, used as
   * the children's key: React tears down the old form and mounts a fresh one
   * in that same commit, so the reader never sees the abandoned order and no
   * animation callback has to fire for it. Bumping it on OPEN rather than on
   * close is what keeps the closing dialog from emptying out while it is
   * still on screen fading.
   */
  const visit = useRef(0);
  const wasVisible = useRef(false);
  if (isModalVisible && !wasVisible.current) visit.current += 1;
  wasVisible.current = isModalVisible;

  return (
    <UIModal
      size="lg"
      title={symbol ? `${action} ${symbol}` : action}
      /* True on both steps: the form's Review only shows the summary, and
         nothing reaches the broker until Confirm. */
      description="Nothing is placed until you confirm."
      visible={isModalVisible}
      onCancel={() => setModalVisible(false)}
      /**
       * The form goes away with the dialog, twice over.
       *
       * Ant Design keeps a hidden dialog's children mounted, and the order
       * form holds which STEP is showing — so closing on the review step
       * and pressing Buy again reopened the summary of the order that was
       * abandoned, with no way back to the form but Back. The same mounting
       * kept a half-typed quantity and a stale validation error from one
       * visit to the next.
       *
       * `destroyOnClose` is the prop for that, and on its own it is not
       * enough: rc-dialog only drops the children once its LEAVE ANIMATION
       * reports finishing, and a tab that is not being handed animation
       * frames never gets that report. Caught in the preview pane, where the
       * dialog was left holding `ant-zoom-leave-start` with no `-active` to
       * follow it: the dialog had faded from view, the wrapper was still
       * display:block over the page, and the form inside it was still
       * mounted with the summary on screen. It is the same stall as a
       * backgrounded tab, and it is not a state a trade dialog may depend
       * on being spared.
       *
       * So the visit below is the guarantee and this is the housekeeping:
       * where the animation does complete, the children are freed and the
       * price widget inside them stops polling.
       */
      destroyOnClose
    >
      <Fragment key={visit.current}>{children}</Fragment>
    </UIModal>
  );
};
